import React, { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Select, SelectItem, Checkbox } from '@nextui-org/react';
import { DateRange } from 'rsuite/DateRangePicker';

import MultiSelectWithActions from '../../common/MultiSelectWithActions';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import {
  getSettlementsEvolutionDataOptimized,
  getAvailableEvolutionFilters,
  type EvolutionFilters,
} from 'common/src/api/settlements';
import {
  SettlementSalesData,
  SettlementMethodology,
  SettlementGroupBy,
  SettlementDateField,
} from '../../../types/settlements';
import { Session } from '@supabase/supabase-js';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import { SalesChartTable, SalesExcelButton } from './SalesChartTable';
import { EmptyState } from '../../ui';

interface VarietyEvolutionChartProps {
  methodology: SettlementMethodology;
  groupBy: SettlementGroupBy;
  dateField: SettlementDateField;
  dateRange: DateRange | null;
  selectedMarkets: string[];
  session: Session | null;
  userProfile: any;
  selectedSpecies: any;
  selectedSeason: string;
  visualization?: 'chart' | 'table';
}

const VarietyEvolutionChart: React.FC<VarietyEvolutionChartProps> = ({
  methodology,
  groupBy,
  dateField,
  dateRange,
  selectedMarkets,
  session,
  userProfile,
  selectedSpecies,
  selectedSeason,
  visualization = 'chart',
}) => {
  const { t } = useTranslation('settlementsComparative');
  const { darkMode } = useUserStore();
  // Data and loading states
  const [data, setData] = useState<SettlementSalesData | null>(null);
  const [currency, setCurrency] = useState<string>('USD');
  const [totalKilos, setTotalKilos] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  // Available filters
  const [availableFilters, setAvailableFilters] = useState<EvolutionFilters>({
    varieties: [],
    calibers: [],
    formats: [],
    colors: [],
  });
  const [filtersLoading, setFiltersLoading] = useState(false);

  // Chart states
  const [selectedVarieties, setSelectedVarieties] = useState<string[]>([]);
  const [selectedCaliber, setSelectedCaliber] = useState('');
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [showOwnPrices, setShowOwnPrices] = useState(false);

  const ownExporterId = userProfile?.exporter_id?.toString();

  // Check if user is demo
  const isDemo = !!userProfile?.exporter?.hasDemo;

  // Fetch available filters
  const fetchAvailableFilters = async () => {
    if (
      !session ||
      !userProfile?.exporter_id ||
      !selectedSeason ||
      !selectedSpecies
    ) {
      return;
    }

    if (selectedSpecies.id !== 2) {
      return;
    }

    setFiltersLoading(true);

    const startDate = dateRange
      ? dateRange[0]?.toISOString().split('T')[0]
      : undefined;
    const endDate = dateRange
      ? dateRange[1]?.toISOString().split('T')[0]
      : undefined;

    try {
      const filtersData = await getAvailableEvolutionFilters(
        session,
        selectedSeason,
        selectedSpecies.id,
        startDate,
        endDate,
        dateField,
        selectedMarkets.length > 0 ? selectedMarkets : undefined
      );

      setAvailableFilters(filtersData);

      console.log('Available filters loaded:', {
        varieties: filtersData.varieties.length,
        calibers: filtersData.calibers.length,
        formats: filtersData.formats.length,
        colors: filtersData.colors.length,
      });
    } catch (err) {
      console.error('Error fetching available filters:', err);
    } finally {
      setFiltersLoading(false);
    }
  };

  // Fetch evolution data
  const fetchEvolutionData = async () => {
    if (
      !session ||
      !userProfile?.exporter_id ||
      !selectedSeason ||
      !selectedSpecies
    ) {
      return;
    }

    if (selectedSpecies.id !== 2) {
      return;
    }

    setLoading(true);

    const startDate = dateRange
      ? dateRange[0]?.toISOString().split('T')[0]
      : undefined;
    const endDate = dateRange
      ? dateRange[1]?.toISOString().split('T')[0]
      : undefined;

    try {
      const evolutionData = await getSettlementsEvolutionDataOptimized(
        session,
        userProfile.exporter_id,
        methodology,
        selectedSeason,
        'variety', // evolution grouping by variety
        dateField,
        selectedSpecies.id,
        startDate,
        endDate,
        groupBy,
        selectedMarkets.length > 0 ? selectedMarkets : undefined,
        'none', // separation
        undefined, // no variety filter for variety chart
        selectedCaliber,
        selectedFormats,
        selectedColors
      );

      if (
        evolutionData &&
        typeof evolutionData === 'object' &&
        'data' in evolutionData
      ) {
        // New API response format with currency and total kilos
        setData(evolutionData.data);
        setCurrency(evolutionData.currency || 'USD');
        setTotalKilos(evolutionData.totalKilos || null);
      } else {
        // Fallback for old format
        setData(evolutionData);
        setCurrency('USD');
        setTotalKilos(null);
      }
    } catch (err) {
      console.error('Error fetching evolution data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch filters on mount and when dependencies change
  useEffect(() => {
    fetchAvailableFilters();
  }, [selectedMarkets, dateRange, dateField, selectedSeason, selectedSpecies]);

  // Auto-select first options when available filters change
  useEffect(() => {
    if (
      availableFilters.varieties.length > 0 &&
      selectedVarieties.length === 0
    ) {
      if (isDemo) {
        // For demo users, only select LAPINS if available
        const lapinsVariety = availableFilters.varieties.find(
          (v) => v.value.toUpperCase() === 'LAPINS'
        );
        if (lapinsVariety) {
          console.log('Auto-selecting LAPINS for demo user');
          setSelectedVarieties([lapinsVariety.value]);
        }
      } else {
        const firstVarieties = availableFilters.varieties
          .slice(0, 3)
          .map((v) => v.value);
        console.log('Auto-selecting varieties from useEffect:', firstVarieties);
        setSelectedVarieties(firstVarieties);
      }
    }
    if (availableFilters.calibers.length > 0 && !selectedCaliber) {
      console.log(
        'Auto-selecting caliber from useEffect:',
        availableFilters.calibers[0].value
      );
      setSelectedCaliber(availableFilters.calibers[0].value);
    }
    if (availableFilters.formats.length > 0 && selectedFormats.length === 0) {
      console.log(
        'Auto-selecting formats from useEffect:',
        availableFilters.formats.map((f) => f.value)
      );
      setSelectedFormats(availableFilters.formats.map((f) => f.value));
    }
    if (availableFilters.colors.length > 0 && selectedColors.length === 0) {
      console.log(
        'Auto-selecting colors from useEffect:',
        availableFilters.colors.map((c) => c.value)
      );
      setSelectedColors(availableFilters.colors.map((c) => c.value));
    }
  }, [availableFilters, isDemo]);

  // Fetch data when filters change
  useEffect(() => {
    if (
      selectedCaliber &&
      selectedFormats.length > 0 &&
      selectedColors.length > 0
    ) {
      fetchEvolutionData();
    }
  }, [
    methodology,
    groupBy,
    selectedMarkets,
    dateRange,
    dateField,
    selectedCaliber,
    selectedFormats,
    selectedColors,
  ]);

  const processedData = useMemo((): SettlementSalesData | null => {
    if (!data) {
      return null;
    }

    // Both methodologies now return the same structure
    return data as SettlementSalesData;
  }, [data, methodology, ownExporterId]);

  const prepareVarietyEvolutionChartData = (
    chartData: SettlementSalesData | null,
    varietiesFilter: string[]
  ) => {
    if (!chartData) {
      return { categories: [], series: [] };
    }

    const varietiesToShow =
      varietiesFilter.length > 0 ? varietiesFilter : Object.keys(chartData);

    // Get all date keys from all varieties
    const allDateKeys = new Set<string>();
    varietiesToShow.forEach((variety) => {
      if (chartData[variety]) {
        Object.keys(chartData[variety]).forEach((dateKey) =>
          allDateKeys.add(dateKey)
        );
      }
    });

    const dateKeys = Array.from(allDateKeys).sort();

    // Create series for each variety
    const series: any[] = [];

    varietiesToShow.forEach((variety, index) => {
      const baseColor = `hsl(${
        index * (360 / varietiesToShow.length)
      }, 70%, 50%)`;
      const lightColor = `hsl(${
        index * (360 / varietiesToShow.length)
      }, 70%, 80%)`;

      const varietyData = chartData[variety];
      if (!varietyData) return;

      // Own data series - only include if showOwnPrices is true
      if (showOwnPrices) {
        const ownData: Array<{ x: string; y: number | null }> = [];

        dateKeys.forEach((dateKey) => {
          const dateData = varietyData[dateKey];
          let ownPrice = null;

          if (dateData) {
            // Aggregate all variety entries for this date
            let ownTotalKilos = 0;
            let ownTotalSales = 0;

            Object.values(dateData).forEach((varietyEntryData: any) => {
              // Calculate own aggregated price
              if (
                varietyEntryData.own &&
                Array.isArray(varietyEntryData.own) &&
                varietyEntryData.own.length > 0 &&
                varietyEntryData.ownKg &&
                varietyEntryData.ownKg > 0
              ) {
                const ownValue = varietyEntryData.own[0]?.value;
                if (ownValue && typeof ownValue === 'number') {
                  ownTotalKilos += varietyEntryData.ownKg;
                  ownTotalSales += ownValue * varietyEntryData.ownKg;
                }
              }
            });

            // Calculate weighted average prices
            if (ownTotalKilos > 0) {
              ownPrice = ownTotalSales / ownTotalKilos;
            }
          }

          ownData.push({ x: dateKey, y: ownPrice });
        });

        // Add own series (solid line, lighter color)
        series.push({
          name: `${variety} - ${t('Tu Exportadora')}`,
          data: ownData,
          color: lightColor,
          type: 'line',
          stroke: {
            width: 3,
            dashArray: 0, // Solid line
          },
        });
      }

      // Others data series - always included
      const othersData: Array<{ x: string; y: number | null }> = [];

      dateKeys.forEach((dateKey) => {
        const dateData = varietyData[dateKey];
        let othersPrice = null;

        if (dateData) {
          // Aggregate all variety entries for this date
          let othersTotalKilos = 0;
          let othersTotalSales = 0;

          Object.values(dateData).forEach((varietyEntryData: any) => {
            // Calculate others aggregated price (using median)
            if (
              varietyEntryData.others &&
              varietyEntryData.others.median &&
              varietyEntryData.others.median > 0 &&
              varietyEntryData.othersKg &&
              varietyEntryData.othersKg > 0
            ) {
              othersTotalKilos += varietyEntryData.othersKg;
              othersTotalSales +=
                varietyEntryData.others.median * varietyEntryData.othersKg;
            }
          });

          // Calculate weighted average prices
          if (othersTotalKilos > 0) {
            othersPrice = othersTotalSales / othersTotalKilos;
          }
        }

        othersData.push({ x: dateKey, y: othersPrice });
      });

      // Add others series (dashed line, base color)
      series.push({
        name: `${variety} - ${
          methodology === 'by_volume' ? t('Mercado') : t('Otras Exportadoras')
        }`,
        data: othersData,
        color: baseColor,
        type: 'line',
        stroke: {
          width: 2,
          dashArray: 0, // Solid line
        },
      });
    });

    return {
      categories: dateKeys,
      series: series,
    };
  };

  const chartData = useMemo(
    () => prepareVarietyEvolutionChartData(processedData, selectedVarieties),
    [processedData, selectedVarieties, showOwnPrices]
  );

  const chartOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'line',
        height: 400,
        foreColor: darkMode ? '#A1A1AA' : '#71717A',
        zoom: {
          enabled: true,
        },
        toolbar: {
          show: true,
        },
      },
      stroke: {
        curve: 'smooth',
        width: chartData.series.map((s) => s.stroke?.width || 2),
        dashArray: chartData.series.map((s) => s.stroke?.dashArray || 0),
      },
      xaxis: {
        categories: chartData.categories,
        title: {
          text: groupBy === 'week' ? t('Semana') : t('Día'),
        },
        labels: {
          formatter: (value) => {
            if (typeof value !== 'string') {
              return value;
            }
            if (groupBy === 'week') {
              const parts = value.split('-W');
              return parts.length > 1 ? parts[1] : value;
            } else {
              const parts = value.split('-');
              if (parts.length === 3) {
                return `${parts[2]}/${parts[1]}`;
              }
              return value;
            }
          },
          rotate: -45,
        },
      },
      yaxis: {
        title: {
          text: `${t('Precio')} (${currency})`,
        },
        labels: {
          formatter: (value) => {
            if (!value) return '';
            const symbol = currency === 'RMB' ? '¥' : 'USD';
            return `${value.toFixed(2)} ${symbol}`;
          },
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (value) =>
            value ? `$${value.toFixed(2)}/kg` : t('Sin datos'),
        },
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
      },
      markers: {
        size: 4,
        hover: {
          size: 6,
        },
      },
      grid: {
        show: true,
        borderColor: '#e0e0e0',
      },
      colors: chartData.series.map((s) => s.color),
    }),
    [chartData.categories, chartData.series, groupBy]
  );

  // Show loading state
  if (loading || filtersLoading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <p className='text-default-400'>
          {filtersLoading
            ? t('Cargando filtros...')
            : t('Cargando datos de evolución...')}
        </p>
      </div>
    );
  }

  // Show no data state
  if (!data) {
    return (
      <EmptyState title={t('No hay datos de evolución disponibles')} />
    );
  }

  return (
    <div className='w-full'>
      {/* Chart Title */}
      <div className='mb-4'>
        <div className='flex items-start justify-between gap-3'>
          <h3 className='text-lg font-semibold'>
            {t('Evolución de Precios de Venta por Variedad')}
          </h3>
          <SalesExcelButton
            categories={chartData.categories}
            series={chartData.series}
            currency={currency}
            fileName={`precios_evolucion_variedad_${selectedCaliber || 'data'}.xlsx`}
            label={t('Exportar a Excel')}
          />
        </div>
        <p className='text-default-500 text-sm'>
          {t('Moneda')}: {currency}
        </p>
        {totalKilos && (
          <p className='text-default-500 text-sm mt-1'>
            {t('Muestra')}: {totalKilos.toLocaleString('es-CL')} kg
          </p>
        )}
      </div>

      {/* Specific Filters Section */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-4 mb-6'>
        <MultiSelectWithActions
          label={
            isDemo
              ? t('Variedades (Demo: Solo Lapins)')
              : t('Variedades a mostrar')
          }
          placeholder={
            isDemo
              ? t('Demo: Solo Lapins disponible')
              : t('Selecciona variedades')
          }
          options={(isDemo
            ? availableFilters.varieties.filter(
                (v) => v.value.toUpperCase() === 'LAPINS'
              )
            : availableFilters.varieties
          ).map((variety) => ({
            value: variety.value,
            label: variety.value,
          }))}
          selectedKeys={selectedVarieties}
          onSelectionChange={(keys) => {
            if (!isDemo) {
              setSelectedVarieties(keys);
            }
          }}
          size='md'
          isDisabled={isDemo}
        />

        <Select
          label={t('Calibre')}
          placeholder={t('Selecciona un calibre')}
          selectedKeys={
            selectedCaliber ? new Set([selectedCaliber]) : new Set()
          }
          onSelectionChange={(keys) => {
            const selected = Array.from(keys)[0] as string;
            if (selected) setSelectedCaliber(selected);
          }}
          size='md'
        >
          {availableFilters.calibers.map((caliber) => (
            <SelectItem
              key={caliber.value}
              value={caliber.value}
              textValue={caliber.value}
            >
              {caliber.value}
            </SelectItem>
          ))}
        </Select>

        <MultiSelectWithActions
          label={t('Formato')}
          placeholder={t('Selecciona formatos')}
          options={availableFilters.formats.map((format) => ({
            value: format.value,
            label: format.value,
          }))}
          selectedKeys={selectedFormats}
          onSelectionChange={setSelectedFormats}
          size='md'
        />

        <MultiSelectWithActions
          label={t('Color')}
          placeholder={t('Selecciona colores')}
          options={availableFilters.colors.map((color) => ({
            value: color.value,
            label: color.value,
          }))}
          selectedKeys={selectedColors}
          onSelectionChange={setSelectedColors}
          size='md'
        />
      </div>

      {/* Chart Controls */}
      <div className='flex items-center justify-end mb-6'>
        <Checkbox
          isSelected={showOwnPrices}
          onValueChange={setShowOwnPrices}
          size='md'
          color='primary'
        >
          {t('Mostrar mis precios')}
        </Checkbox>
      </div>

      <div className=''>
        {visualization === 'table' ? (
          <SalesChartTable
            categories={chartData.categories}
            series={chartData.series}
            currency={currency}
            emptyLabel={t(
              'No hay datos disponibles para las variedades seleccionadas'
            )}
          />
        ) : chartData.series.length > 0 ? (
          <ReactApexChart
            options={chartOptions}
            series={chartData.series}
            type='line'
            height={400}
          />
        ) : (
          <div className='flex items-center justify-center h-64'>
            <p className='text-default-400'>
              {t('No hay datos disponibles para las variedades seleccionadas')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VarietyEvolutionChart;
